/**
 * Pure lifecycle state machine for the live-updates WebSocket.
 *
 * `wsReducer(state, event) -> [nextState, commands[]]` holds ALL connection
 * logic and performs no I/O. The impure shell (`use-websocket.ts`) interprets
 * the commands against a real socket + timers and feeds socket/timer events
 * back in. Because it is pure it is exhaustively unit-tested with no mocks.
 *
 * Timing lives here: every `SCHEDULE` command carries its own `delayMs`, and a
 * `jitter` flag when the shell should spread that delay. The delay itself stays
 * deterministic, so backoff growth is assertable at this layer.
 */
import {
    BACKOFF_BASE_MS,
    BACKOFF_CAP_MS,
    HEARTBEAT_INTERVAL_MS,
    PHASE_TIMEOUT_MS,
    PROACTIVE_RECONNECT_MS,
    RECEIPT_FLUSH_INTERVAL_MS,
    type WsCommand,
    type WsEvent,
    type WsPhase,
    type WsState,
} from 'src/services/websocket/ws-protocol';
import {
    collectReceipt,
    createReceiptState,
    drainReceiptBatch,
} from 'src/services/websocket/ws-receipts';

// Resets may share this one empty receipt state: every update in
// ws-receipts.ts is copy-on-write, so the embedded array is never mutated.
export const initialWsState: WsState = {
    phase: 'idle',
    attempt: 0,
    token: null,
    enabled: false,
    refresh: 'idle',
    refreshAttempt: 0,
    receipts: createReceiptState(),
};

/** Pure projection of the live signal; the shell diffs this to publish it. */
export function isLive(state: WsState): boolean {
    return state.phase === 'live';
}

/** Deterministic exponential backoff base delay for a given attempt, capped. */
export function backoffDelay(attempt: number): number {
    return Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** attempt);
}

/** No socket exists and none is being established — safe to fresh-start. */
const isDormant = (phase: WsPhase): boolean => phase === 'idle' || phase === 'closed';
/** A socket is opening or authenticating (not yet serving frames). */
const isConnecting = (phase: WsPhase): boolean => phase === 'connecting' || phase === 'authenticating';
/** A socket is established or being established, so a disconnect is meaningful. */
const isActive = (phase: WsPhase): boolean => isConnecting(phase) || phase === 'live';

const clearAllTimers = (): WsCommand => ({ type: 'CLEAR' });

const schedulePhaseTimeout = (): WsCommand => ({ type: 'SCHEDULE', timer: 'phase', delayMs: PHASE_TIMEOUT_MS, jitter: false });
const scheduleHeartbeat = (): WsCommand => ({ type: 'SCHEDULE', timer: 'heartbeat', delayMs: HEARTBEAT_INTERVAL_MS, jitter: false });
const scheduleProactive = (): WsCommand => ({ type: 'SCHEDULE', timer: 'proactive', delayMs: PROACTIVE_RECONNECT_MS, jitter: false });
const scheduleReconnect = (attempt: number): WsCommand => ({ type: 'SCHEDULE', timer: 'reconnect', delayMs: backoffDelay(attempt), jitter: true });
const scheduleRefreshRetry = (attempt: number): WsCommand => ({ type: 'SCHEDULE', timer: 'refreshRetry', delayMs: backoffDelay(attempt), jitter: true });
const scheduleReceiptFlush = (): WsCommand => ({ type: 'SCHEDULE', timer: 'receiptFlush', delayMs: RECEIPT_FLUSH_INTERVAL_MS, jitter: false });

// AUTH_ERROR pre-increments `attempt` before the refresh runs, so the reconnect
// after a settled auth refresh uses the rejected attempt's delay.
const scheduleAuthReconnect = (state: WsState): WsCommand => scheduleReconnect(Math.max(0, state.attempt - 1));

const openSocketCommands = (): WsCommand[] => [schedulePhaseTimeout(), { type: 'OPEN_SOCKET' }];

/** Tear the current socket + timers down and immediately open a fresh one. */
const reconnectNowCommands = (): WsCommand[] => [
    { type: 'CLOSE_SOCKET' },
    clearAllTimers(),
    ...openSocketCommands(),
];

/** Common teardown-then-backoff transition for connection failures. */
function enterBackoff(state: WsState, attempt: number, extra: WsCommand[]): [WsState, WsCommand[]] {
    return [
        { ...state, phase: 'backoff', attempt, refresh: 'idle', refreshAttempt: 0 },
        [{ type: 'CLOSE_SOCKET' }, clearAllTimers(), ...extra],
    ];
}

export function wsReducer(state: WsState, event: WsEvent): [WsState, WsCommand[]] {
    switch (event.type) {
        case 'ENABLE': {
            const next = { ...state, enabled: true };
            // The shell normally follows ENABLE with TOKEN_READY (which opens),
            // so `token` is still null here. But if a token was already stored
            // (a TOKEN_READY that arrived while disabled), open now — enabling must
            // never strand us with a token and no socket regardless of event order.
            if (state.token && isDormant(state.phase)) {
                return [{ ...next, phase: 'connecting' }, openSocketCommands()];
            }
            return [next, []];
        }

        case 'DISABLE': {
            // Already fully torn down — emit nothing so the flag-OFF kill switch
            // registers no socket and no timers.
            if (!state.enabled && isDormant(state.phase)) {
                return [state, []];
            }
            // Best-effort final flush, ordered before CLOSE_SOCKET so the shell
            // still holds the socket. Only a live socket can carry it: a batch
            // stranded in connect/backoff is dropped with the teardown
            // (at-most-once telemetry), mirroring the PAGE_HIDE guard.
            const finalReceipt = state.phase === 'live' ?
                drainReceiptBatch(state.receipts).frame :
                null;
            return [
                { ...initialWsState, phase: 'closed' },
                [
                    ...finalReceipt ?
                        [{ type: 'SEND_RECEIPT', frame: finalReceipt } satisfies WsCommand] :
                        [],
                    { type: 'CLOSE_SOCKET' },
                    clearAllTimers(),
                ],
            ];
        }

        case 'TOKEN_READY': {
            const next = { ...state, token: event.token };
            if (!state.enabled) {
                return [next, []];
            }
            // Fresh start: open a socket.
            if (isDormant(state.phase)) {
                return [{ ...next, phase: 'connecting' }, openSocketCommands()];
            }
            // Backoff owns its delay. An external token update may replace the
            // stored credential, but cannot bypass auth-rejection backoff or
            // masquerade as completion of an in-flight refresh.
            if (state.phase === 'backoff') {
                return [next, []];
            }
            // Active socket (connecting / authenticating / live). The current
            // socket is (or will be) authed with `state.token`, so compare:
            //  - Same token: an `online` nudge re-supplying the value we already
            //    hold — keep the healthy connection, don't churn it.
            //  - Changed token: a team switch creates a new ws_token (or a refresh
            //    rotated it), so the socket is authed to the *wrong* (prior)
            //    token. Tear it down and reconnect to re-auth under the new one;
            //    reset `attempt` since this is an intentional switch, not a
            //    failure.
            if (event.token === state.token) {
                return [next, []];
            }
            return [{ ...next, phase: 'connecting', attempt: 0 }, reconnectNowCommands()];
        }

        case 'SOCKET_OPEN':
            if (state.phase === 'connecting' && state.token) {
                return [
                    { ...state, phase: 'authenticating' },
                    [schedulePhaseTimeout(), { type: 'SEND', frame: { action: 'auth', token: state.token } }],
                ];
            }
            return [state, []];

        case 'OPEN_FAILED':
            // The shell could not construct a socket — a missing or malformed
            // server URL throws synchronously and no open/close/error will ever
            // arrive. That is a static-config failure, so a timer retry would
            // spin forever; fall back to `closed` (keeping `enabled`/`token`) so
            // a later ENABLE / TOKEN_READY (login, flag toggle, `online`) can
            // still retry on a discrete event rather than a busy loop.
            if (state.phase === 'connecting') {
                return [
                    { ...state, phase: 'closed' },
                    [{ type: 'CLOSE_SOCKET' }, clearAllTimers()],
                ];
            }
            return [state, []];

        case 'AUTH_OK':
            if (state.phase === 'authenticating') {
                return [
                    { ...state, phase: 'live', attempt: 0, refresh: 'idle', refreshAttempt: 0 },
                    [
                        { type: 'CLEAR', timer: 'phase' },
                        scheduleHeartbeat(),
                        scheduleProactive(),
                        scheduleReceiptFlush(),
                    ],
                ];
            }
            return [state, []];

        case 'AUTH_ERROR':
            // Only the token currently awaiting authentication can be rejected.
            // Invalidate it immediately; refresh completion schedules the delay,
            // so neither a racing external token nor refresh can bypass backoff.
            if (state.phase === 'authenticating') {
                return [
                    {
                        ...state,
                        phase: 'backoff',
                        attempt: state.attempt + 1,
                        token: null,
                        refresh: 'auth',
                        refreshAttempt: 0,
                    },
                    [{ type: 'CLOSE_SOCKET' }, clearAllTimers(), { type: 'REFRESH_TOKEN' }],
                ];
            }
            return [state, []];

        case 'REFRESH_SUCCEEDED':
            if (state.refresh === 'auth' && state.phase === 'backoff') {
                return [
                    { ...state, token: event.token, refresh: 'idle' },
                    [scheduleAuthReconnect(state)],
                ];
            }
            if (state.refresh === 'proactive' && state.phase === 'live') {
                return [
                    {
                        ...state,
                        phase: 'connecting',
                        attempt: 0,
                        token: event.token,
                        refresh: 'idle',
                        refreshAttempt: 0,
                    },
                    reconnectNowCommands(),
                ];
            }
            return [state, []];

        case 'REFRESH_FAILED':
            if (state.refresh === 'auth' && state.phase === 'backoff') {
                return [
                    { ...state, refresh: 'idle' },
                    [scheduleAuthReconnect(state)],
                ];
            }
            if (state.refresh === 'proactive' && state.phase === 'live') {
                return [
                    {
                        ...state,
                        refresh: 'proactive-wait',
                        refreshAttempt: state.refreshAttempt + 1,
                    },
                    [scheduleRefreshRetry(state.refreshAttempt)],
                ];
            }
            return [state, []];

        case 'CLOSE':
        case 'ERROR':
            // Only an active socket produces a real disconnect; ignoring these in
            // backoff/idle/closed keeps a duplicate CLOSE from double-incrementing.
            if (isActive(state.phase)) {
                return enterBackoff(state, state.attempt + 1, [scheduleReconnect(state.attempt)]);
            }
            return [state, []];

        case 'HEARTBEAT_TICK':
            if (state.phase === 'live') {
                return [state, [{ type: 'SEND', frame: { action: 'heartbeat' } }, scheduleHeartbeat()]];
            }
            return [state, []];

        case 'RECONNECT_TICK':
            if (state.phase === 'backoff') {
                if (!state.token) {
                    return [{ ...state, refresh: 'auth' }, [{ type: 'REFRESH_TOKEN' }]];
                }
                return [{ ...state, phase: 'connecting' }, openSocketCommands()];
            }
            return [state, []];

        case 'REFRESH_RETRY_TICK':
            if (state.phase === 'live' && state.refresh === 'proactive-wait') {
                return [{ ...state, refresh: 'proactive' }, [{ type: 'REFRESH_TOKEN' }]];
            }
            return [state, []];

        case 'PROACTIVE_TICK':
            // Keep serving frames on the healthy socket until replacement
            // credentials are ready. Refresh failures retry independently.
            if (state.phase === 'live' && state.refresh === 'idle') {
                return [{ ...state, refresh: 'proactive', refreshAttempt: 0 }, [{ type: 'REFRESH_TOKEN' }]];
            }
            return [state, []];

        case 'PHASE_TIMEOUT':
            if (isConnecting(state.phase)) {
                return enterBackoff(state, state.attempt + 1, [scheduleReconnect(state.attempt)]);
            }
            return [state, []];

        case 'FRAME':
            // Forward data frames to consumers only while live. Stray frames
            // before `live` are ignored, not buffered. Receipt collection rides
            // along but can never block or reorder the emit.
            if (state.phase === 'live') {
                const receipts = collectReceipt(state.receipts, event.frame);
                return [
                    receipts === state.receipts ? state : { ...state, receipts },
                    [{ type: 'EMIT_FRAME', frame: event.frame }],
                ];
            }
            return [state, []];

        case 'RECEIPT_FLUSH_TICK': {
            if (state.phase !== 'live') {
                return [state, []];
            }
            const drained = drainReceiptBatch(state.receipts);
            if (!drained.frame) {
                return [state, [scheduleReceiptFlush()]];
            }
            return [
                { ...state, receipts: drained.state },
                [{ type: 'SEND_RECEIPT', frame: drained.frame }, scheduleReceiptFlush()],
            ];
        }

        case 'PAGE_HIDE': {
            // Only a live socket can carry the flush. Otherwise keep the batch
            // pending: a page restored from bfcache reconnects and flushes on
            // the normal cadence.
            if (state.phase !== 'live') {
                return [state, []];
            }
            const drained = drainReceiptBatch(state.receipts);
            if (!drained.frame) {
                return [state, []];
            }
            return [
                { ...state, receipts: drained.state },
                [{ type: 'SEND_RECEIPT', frame: drained.frame }],
            ];
        }

        default: {
            // Exhaustiveness guard: a new event type must be handled above.
            const _: never = event;
            void _;
            return [state, []];
        }
    }
}
